/**
 * Runtime configuration shim.
 *
 * Goal: one bundle deploys to demo / staging / production. The portal reads
 * its environment from `window.__APP_CONFIG__` (populated by `/config.js`,
 * which nginx serves as a small dynamic file rendered from env vars at
 * container startup).
 *
 * Build-time `import.meta.env.VITE_*` is kept as a fallback so:
 *   - `pnpm dev` keeps working from `.env.local`
 *   - existing built bundles that ship without `/config.js` don't break.
 *
 * Resolution order:
 *   window.__APP_CONFIG__.<KEY>   →   import.meta.env.VITE_<KEY>   →   default
 */

interface AppConfig {
  KEYCLOAK_URL?: string
  KEYCLOAK_REALM?: string
  KEYCLOAK_CLIENT_ID?: string
  EMPLOYEE_API_URL?: string
}

declare global {
  interface Window {
    __APP_CONFIG__?: AppConfig
  }
}

function pick(key: keyof AppConfig, viteKey: string, fallback: string): string {
  const runtime = (typeof window !== 'undefined' && window.__APP_CONFIG__?.[key]) || ''
  if (runtime) return runtime
  // Vite replaces `import.meta.env.VITE_*` at build time with literal strings.
  const fromVite = (import.meta.env as Record<string, string | undefined>)[viteKey]
  if (fromVite) return fromVite
  return fallback
}

export const RUNTIME_CONFIG = {
  KEYCLOAK_URL: pick('KEYCLOAK_URL', 'VITE_KEYCLOAK_URL', ''),
  KEYCLOAK_REALM: pick('KEYCLOAK_REALM', 'VITE_KEYCLOAK_REALM', 'employee'),
  KEYCLOAK_CLIENT_ID: pick('KEYCLOAK_CLIENT_ID', 'VITE_KEYCLOAK_CLIENT_ID', 'employee-portal'),
  EMPLOYEE_API_URL: pick('EMPLOYEE_API_URL', 'VITE_EMPLOYEE_API_URL', ''),
} as const
